import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, User, Share2, Linkedin, Twitter, ChevronDown, ChevronRight, List, ArrowRight, Loader2 } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://finease-crm.vercel.app';

const categoryRelatedServices = {
  "Fundraising": [
    { id: "startup-india", label: "Startup India Registration", path: "/services/startup-india" },
    { id: "private-limited", label: "Company Registration", path: "/services/private-limited-company" },
    { id: "virtual-cfo", label: "Virtual CFO", path: "/services/virtual-cfo" },
  ],
  "Tax Strategy": [
    { id: "income-tax-filing", label: "ITR Filing", path: "/services/income-tax-filing" },
    { id: "tax-planning", label: "Tax Planning", path: "/services/tax-planning" },
    { id: "gst-registration", label: "GST Registration", path: "/services/gst-registration" },
  ],
  "CFO Insights": [
    { id: "virtual-cfo", label: "Virtual CFO", path: "/services/virtual-cfo" },
    { id: "compliance-manager", label: "Compliance Manager", path: "/services/compliance-manager" },
    { id: "annual-filing", label: "Annual Filing", path: "/services/annual-filing" },
  ],
  "Finance": [
    { id: "virtual-cfo", label: "Virtual CFO", path: "/services/virtual-cfo" },
    { id: "gst-return-filing", label: "GST Returns", path: "/services/gst-return-filing" },
    { id: "tds-filing", label: "TDS Filing", path: "/services/tds-filing" },
  ],
};

const servicePathMapping = {
  "startup-india": "/services/startup-india",
  "private-limited": "/services/private-limited-company",
  "virtual-cfo": "/services/virtual-cfo",
  "income-tax-filing": "/services/income-tax-filing",
  "tax-planning": "/services/tax-planning",
  "gst-registration": "/services/gst-registration",
  "compliance-manager": "/services/compliance-manager",
  "annual-filing": "/services/annual-filing",
  "gst-return-filing": "/services/gst-return-filing",
  "tds-filing": "/services/tds-filing",
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/blogs/${slug}`);

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Blog not found');
          }
          throw new Error('Failed to fetch blog');
        }

        const data = await response.json();
        setPost(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching blog:', err);
        setError(err.message);
        // Try fallback data
        if (fallbackBlogPosts[slug]) {
          setPost(fallbackBlogPosts[slug]);
          setError(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [slug]);

  // Extract TOC from content
  const extractToc = (content) => {
    if (!content) return [];
    const headings = [];
    const headingRegex = /<h([23])[^>]*>(.*?)<\/h[23]>/gi;
    let match;
    while ((match = headingRegex.exec(content)) !== null) {
      const level = parseInt(match[1]);
      const text = match[2].replace(/<[^>]*>/g, '');
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      headings.push({ id, text, level });
    }
    // Add FAQ to TOC if present
    const faqs = post?.faqs || [];
    if (faqs.length > 0) {
      headings.push({ id: 'faq', text: 'Frequently Asked Questions', level: 2 });
    }
    return headings;
  };

  // Add IDs to h2 and h3 elements
  const addIdsToContent = (content) => {
    if (!content) return '';
    let modifiedContent = content;
    const headingRegex = /<h([2-4])>(.*?)<\/h[2-4]>/gi;
    modifiedContent = modifiedContent.replace(headingRegex, (match, level, text) => {
      const cleanText = text.replace(/<[^>]*>/g, '');
      const id = cleanText.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      return `<h${level} id="${id}">${text}</h${level}>`;
    });
    return modifiedContent;
  };

  // Loading state
  useEffect(() => {
    // Transform PDF links into responsive embeds (Iframe on Desktop, Link on Mobile)
    if (post) {
      setTimeout(() => {
        const container = document.querySelector('.blog-content');
        if (!container) return;

        const pdfLinks = container.querySelectorAll('a.pdf-link');
        pdfLinks.forEach(link => {
          if (link.getAttribute('data-processed')) return;

          const url = link.href;

          // Create wrapper
          const wrapper = document.createElement('div');
          wrapper.className = 'my-8';

          // 1. Desktop View (Iframe)
          const iframeContainer = document.createElement('div');
          iframeContainer.className = 'hidden md:block w-full h-[800px] border border-slate-700 rounded-lg overflow-hidden bg-slate-800';

          const iframe = document.createElement('iframe');
          iframe.src = url;
          iframe.className = 'w-full h-full';
          iframe.setAttribute('type', 'application/pdf');

          iframeContainer.appendChild(iframe);

          // 2. Mobile View (Original styled link)
          const mobileContainer = document.createElement('div');
          mobileContainer.className = 'md:hidden';
          mobileContainer.appendChild(link.cloneNode(true));

          // Assemble
          wrapper.appendChild(iframeContainer);
          wrapper.appendChild(mobileContainer);

          // Replace
          link.parentNode.replaceChild(wrapper, link);
          link.setAttribute('data-processed', 'true');
        });
      }, 100);
    }
  }, [post]);

  if (loading) {
    return (
      <main className="pt-32 pb-24 min-h-screen bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00C896] mx-auto" />
          <p className="mt-4 text-slate-400">Loading blog post...</p>
        </div>
      </main>
    );
  }

  // Not found state
  if (!post) {
    return (
      <main className="pt-32 pb-24 min-h-screen bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Post Not Found</h1>
          <p className="text-slate-400 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#00C896] font-medium"
          >
            <ArrowLeft size={16} />
            Back to Blog
          </Link>
        </div>
      </main>
    );
  }

  const tocItems = extractToc(post.content);
  const contentWithIds = addIdsToContent(post.content);
  const faqs = post.faqs || [];

  // Get related services - from API relatedServices or fallback to category mapping
  let relatedServices = [];
  if (post.relatedServices && post.relatedServices.length > 0) {
    relatedServices = post.relatedServices.map(id => ({
      id,
      label: id.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      path: servicePathMapping[id] || `/services/${id}`
    }));
  } else if (categoryRelatedServices[post.category]) {
    relatedServices = categoryRelatedServices[post.category];
  }

  return (
    <main data-testid="blog-post-page" className="pt-24 md:pt-32 bg-[#050505]">
      {/* Back Link */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 mb-8">
        <Link
          to="/blog"
          data-testid="back-to-blog"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Blog
        </Link>
      </div>

      {/* Header */}
      <header className="max-w-4xl mx-auto px-6 md:px-12 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896] mb-4 block">
            {post.category}
          </span>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {post.title}
          </h1>
          <p className="text-xl text-slate-400 leading-relaxed mb-8">
            {post.excerpt}
          </p>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#00C896]/20 flex items-center justify-center">
                <span className="text-[#00C896] font-semibold">
                  {post.author?.charAt(0) || 'A'}
                </span>
              </div>
              <div>
                <p className="text-white font-medium">{post.author}</p>
                <p className="text-slate-500 text-xs">{post.authorRole}</p>
              </div>
            </div>
            <span className="flex items-center gap-2">
              <Calendar size={14} />
              {post.date}
            </span>
            <span className="flex items-center gap-2">
              <Clock size={14} />
              {post.readTime}
            </span>
          </div>
        </motion.div>
      </header>

      {/* Featured Image */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="max-w-5xl mx-auto px-6 md:px-12 mb-12"
      >
        <div className="aspect-video rounded-2xl overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      </motion.div>

      {/* Two Column Layout: TOC + Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Table of Contents - Sticky Sidebar */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="p-5 rounded-xl bg-[#0A0A0A] border border-white/5"
              >
                <div className="flex items-center gap-2 mb-4">
                  <List className="w-4 h-4 text-[#00C896]" />
                  <h3 className="text-sm font-semibold text-white">Table of Contents</h3>
                </div>
                <nav className="space-y-2">
                  {tocItems.map((item, index) => (
                    <a
                      key={index}
                      href={`#${item.id}`}
                      className={`block text-sm text-slate-400 hover:text-[#00C896] transition-colors py-1 ${item.level === 3 ? 'pl-4 text-xs' : ''}`}
                    >
                      {item.text}
                    </a>
                  ))}
                </nav>
              </motion.div>

              {/* Related Services Widget */}
              {relatedServices.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="mt-6 p-5 rounded-xl bg-[#0A0A0A] border border-white/5"
                >
                  <h3 className="text-sm font-semibold text-white mb-4">Related Services</h3>
                  <div className="space-y-2">
                    {relatedServices.map((service) => (
                      <Link
                        key={service.id}
                        to={service.path}
                        className="flex items-center justify-between py-2 text-sm text-slate-400 hover:text-[#00C896] transition-colors group"
                      >
                        <span>{service.label}</span>
                        <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <article className="lg:col-span-9">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="prose prose-invert prose-lg max-w-none
                prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-white prose-headings:scroll-mt-24
                prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-2
                prose-p:text-slate-400 prose-p:leading-relaxed
                prose-li:text-slate-400
                prose-strong:text-white prose-strong:font-medium
                prose-blockquote:border-l-[#00C896] prose-blockquote:text-slate-300 prose-blockquote:italic prose-blockquote:bg-white/5 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-lg
                prose-ul:space-y-2
                prose-a:text-[#00C896] prose-a:no-underline hover:prose-a:underline
                prose-hr:border-white/10
                blog-content"
              dangerouslySetInnerHTML={{ __html: contentWithIds }}
            />
            {/* Styles for embedded YouTube videos and PDF links in blog content */}
            <style>{`
              .blog-content .yt-embed {
                position: relative;
                padding-bottom: 56.25%;
                height: 0;
                overflow: hidden;
                max-width: 100%;
                margin: 1.5em 0;
                border-radius: 12px;
                border: 1px solid rgba(255,255,255,0.1);
              }
              .blog-content .yt-embed iframe {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                border: none;
                border-radius: 12px;
              }
              .blog-content a.pdf-link {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                color: #f87171 !important;
                font-weight: 500;
                text-decoration: none !important;
                padding: 6px 14px;
                border: 1px solid rgba(248,113,113,0.2);
                border-radius: 8px;
                background: rgba(248,113,113,0.05);
                transition: background 0.2s;
              }
              .blog-content a.pdf-link:hover {
                background: rgba(248,113,113,0.12);
                text-decoration: none !important;
              }

                .blog-content table {
                  width: 100%;
                  border-collapse: collapse;
                  margin: 1.25rem 0;
                  font-size: 0.95rem;
                  color: #e2e8f0;
                  display: block;
                  overflow-x: auto;
                }

                .blog-content th,
                .blog-content td {
                  border: 1px solid rgba(148, 163, 184, 0.35);
                  padding: 0.55rem 0.7rem;
                  vertical-align: top;
                  white-space: nowrap;
                }

                .blog-content th {
                  background: rgba(148, 163, 184, 0.14);
                  color: #f8fafc;
                  font-weight: 600;
                }

                .blog-content h3 {
                  font-size: 1.25rem;
                  margin-top: 2rem;
                  margin-bottom: 0.75rem;
                  color: #fff;
                  font-weight: 600;
                }

                .blog-content h4 {
                  font-size: 1.1rem;
                  margin-top: 1.5rem;
                  margin-bottom: 0.5rem;
                  color: #e2e8f0;
                  font-weight: 600;
                }

                .blog-content hr {
                  border: none;
                  border-top: 1px solid rgba(255,255,255,0.1);
                  margin: 2rem 0;
                }

                .blog-content code {
                  background: rgba(255,255,255,0.08);
                  color: #00C896;
                  padding: 2px 6px;
                  border-radius: 4px;
                  font-size: 0.875em;
                  font-family: 'Fira Code', 'Cascadia Code', monospace;
                }

                .blog-content pre {
                  background: rgba(255,255,255,0.05);
                  border: 1px solid rgba(255,255,255,0.1);
                  border-radius: 8px;
                  padding: 1rem 1.25rem;
                  overflow-x: auto;
                  font-size: 0.9rem;
                  line-height: 1.6;
                }

                .blog-content pre code {
                  background: none;
                  padding: 0;
                  color: #e2e8f0;
                }

                .blog-content mark {
                  background: rgba(250, 204, 21, 0.25);
                  color: #fef08a;
                  padding: 1px 4px;
                  border-radius: 3px;
                }

                .blog-content ul ul,
                .blog-content ol ol,
                .blog-content ul ol,
                .blog-content ol ul {
                  margin-top: 0.25rem;
                  margin-bottom: 0.25rem;
                }
            `}</style>

            {/* Expert Note */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 p-6 rounded-xl bg-[#00C896]/5 border border-[#00C896]/20"
            >
              <p className="text-xs font-mono tracking-wider uppercase text-[#00C896] mb-2">Expert Note</p>
              <p className="text-slate-300 leading-relaxed">
                This article represents the views of our expert team at Finease. Tax laws and regulations
                change frequently. For personalized advice based on your specific situation, please
                consult with our professionals or a qualified advisor.
              </p>
            </motion.div>

            {/* FAQ Section */}
            {faqs.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-16"
              >
                <h2
                  id="faq"
                  className="text-2xl font-semibold text-white mb-6 scroll-mt-24"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  Frequently Asked Questions
                </h2>
                <div className="space-y-3">
                  {faqs.map((faq, index) => (
                    <div
                      key={index}
                      className="rounded-xl border border-white/5 overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-4 text-left bg-[#0A0A0A] hover:bg-white/[0.02] transition-colors"
                      >
                        <span className="text-white font-medium pr-4">{faq.question}</span>
                        <ChevronDown
                          className={`w-5 h-5 text-[#00C896] shrink-0 transition-transform duration-200 ${openFaq === index ? "rotate-180" : ""
                            }`}
                        />
                      </button>
                      {openFaq === index && (
                        <div className="px-4 pb-4 bg-[#0A0A0A]">
                          <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Share */}
            <div className="mt-12 border-t border-white/10 pt-8">
              <div className="flex items-center justify-between">
                <p className="text-slate-400">Share this article</p>
                <div className="flex gap-3">
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="share-twitter"
                    className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-[#00C896] hover:border-[#00C896]/50 transition-colors"
                  >
                    <Twitter size={18} />
                  </a>
                  <a
                    href={`https://linkedin.com/shareArticle?mini=true&title=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="share-linkedin"
                    className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-[#00C896] hover:border-[#00C896]/50 transition-colors"
                  >
                    <Linkedin size={18} />
                  </a>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>

      {/* Related Services Section (Mobile) */}
      {relatedServices.length > 0 && (
        <section className="lg:hidden border-t border-white/5 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
            <h2
              className="text-xl font-semibold text-white mb-6"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Related Services
            </h2>
            <div className="grid grid-cols-1 gap-3">
              {relatedServices.map((service) => (
                <Link
                  key={service.id}
                  to={service.path}
                  className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-[#00C896]/20 transition-colors group"
                >
                  <span className="text-slate-300 group-hover:text-white">{service.label}</span>
                  <ArrowRight className="w-4 h-4 text-[#00C896]" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2
            className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Need Help With Your Business?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Our expert team is ready to help you with company registration, compliance,
            tax planning, and more. Get personalized guidance today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="blog-post-cta"
              className="inline-flex items-center gap-2 bg-[#00C896] text-black font-bold px-8 py-4 rounded-full hover:bg-[#00E6AC] transition-colors"
            >
              Schedule a Consultation
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-medium px-8 py-4 rounded-full hover:bg-white/5 transition-colors"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

// Fallback data in case API is unavailable
const fallbackBlogPosts = {
  "series-a-readiness-checklist": {
    title: "The Ultimate Series A Readiness Checklist for 2025",
    excerpt: "What every founder needs to know before approaching institutional investors.",
    category: "Fundraising",
    author: "Victoria Sterling",
    authorRole: "Managing Partner",
    date: "December 10, 2025",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1762279389020-eeeb69c25813?w=1200&q=80",
    content: `
      <p>Raising a Series A is one of the most significant milestones in a startup's journey. Unlike seed rounds, which often fund potential, Series A investors are looking for proven traction, a clear path to market leadership, and a team capable of executing at scale.</p>

      <h2>Financial Readiness</h2>
      <p>Before approaching Series A investors, ensure your financial house is in order:</p>
      <ul>
        <li><strong>Clean Financials:</strong> Have at least 18-24 months of professionally prepared financial statements</li>
        <li><strong>Unit Economics:</strong> Clear understanding of CAC, LTV, and payback periods</li>
        <li><strong>Revenue Model:</strong> Documented and defendable pricing strategy</li>
        <li><strong>Projections:</strong> 3-5 year financial model with clear assumptions</li>
      </ul>

      <h2>Operational Metrics</h2>
      <p>Investors will scrutinize your key performance indicators:</p>
      <ul>
        <li><strong>Growth Rate:</strong> MoM growth of 15-20% is typically expected</li>
        <li><strong>Retention:</strong> Net revenue retention above 100% is ideal for SaaS</li>
        <li><strong>Market Size:</strong> Clear TAM/SAM/SOM analysis</li>
        <li><strong>Competitive Moat:</strong> Defensible advantages documented</li>
      </ul>

      <h2>Legal & Structural</h2>
      <p>Don't let legal issues derail your fundraise:</p>
      <ul>
        <li>Clean cap table with no unusual provisions</li>
        <li>All IP properly assigned to the company</li>
        <li>Employment agreements in place for all key team members</li>
        <li>No outstanding litigation or regulatory issues</li>
      </ul>

      <h2>The Right Time to Raise</h2>
      <p>Timing your Series A is crucial. The best time to raise is when you have strong momentum, not when you need the money. Plan to start the process 6-9 months before you'll need the capital, as the average Series A takes 3-6 months to close.</p>

      <blockquote>"The best fundraises happen when you don't need the money. Desperation is the enemy of valuation."</blockquote>

      <h2>Conclusion</h2>
      <p>Series A readiness isn't achieved overnight. It requires months of deliberate preparation across financial, operational, and legal dimensions. Start early, engage advisors who have been through the process, and remember that every interaction with potential investors is an opportunity to build relationships for the future.</p>
    `,
    faqs: [
      { question: "What is the ideal revenue for Series A?", answer: "Most VCs look for ₹1-3Cr ARR for B2B SaaS companies, though this varies by industry and growth trajectory." },
      { question: "How long should I prepare before Series A?", answer: "Start preparing 6-9 months before you need the capital. Use this time to strengthen metrics and build investor relationships." },
      { question: "What's the typical Series A timeline?", answer: "From first meeting to closing, expect 3-6 months. Top-tier VCs may move faster for competitive deals." },
      { question: "Do I need a CFO for Series A?", answer: "A fractional CFO can help prepare financials and models, which is often sufficient for Series A." },
    ],
  },
  "qsbs-guide-founders": {
    title: "QSBS Explained: How Founders Can Save Millions in Taxes",
    excerpt: "Qualified Small Business Stock can provide up to ₹10Cr in tax-free gains.",
    category: "Tax Strategy",
    author: "Marcus Chen",
    authorRole: "Tax Strategy Director",
    date: "December 5, 2025",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1762279389002-7b6abd7bd6c6?w=1200&q=80",
    content: `
      <p>Qualified Small Business Stock (QSBS) is one of the most powerful tax benefits available to startup founders and early employees.</p>

      <h2>What is QSBS?</h2>
      <p>Under Section 1202, gains from qualifying stock held for more than 5 years can be excluded from federal income tax.</p>

      <h2>Qualification Requirements</h2>
      <ul>
        <li><strong>C-Corporation Status:</strong> The company must be a domestic C-corp</li>
        <li><strong>Gross Assets:</strong> Company must have gross assets under ₹50Cr at issuance</li>
        <li><strong>Active Business:</strong> At least 80% of assets in active trade</li>
        <li><strong>Holding Period:</strong> Stock must be held for more than 5 years</li>
      </ul>

      <blockquote>"QSBS planning should begin at company formation, not at exit."</blockquote>
    `,
    faqs: [
      { question: "Can I lose QSBS qualification?", answer: "Yes, if the company exceeds ₹50Cr in assets or changes status." },
      { question: "Does QSBS apply to secondary sales?", answer: "No, QSBS only applies to original issuance stock." },
    ],
  },
  "fractional-cfo-guide": {
    title: "When Should Your Startup Hire a Fractional CFO?",
    excerpt: "Understanding the right time to bring in financial leadership.",
    category: "CFO Insights",
    author: "James Okonkwo",
    authorRole: "CFO Services Lead",
    date: "November 28, 2025",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1692607520918-7d8bb85e7e64?w=1200&q=80",
    content: `
      <p>The decision to bring on financial leadership is one of the most impactful choices a founder can make.</p>

      <h2>Signs You Need a Fractional CFO</h2>
      <ul>
        <li><strong>Preparing for Fundraising:</strong> You need investor-grade financials</li>
        <li><strong>Revenue Exceeds $1-2M ARR:</strong> Financial complexity increases</li>
        <li><strong>Board Reporting:</strong> Investors expect professional presentations</li>
      </ul>

      <h2>Fractional vs. Full-Time</h2>
      <p>A fractional CFO provides senior-level leadership at a fraction of the cost.</p>

      <blockquote>"The right CFO doesn't just manage your finances—they become a strategic partner."</blockquote>
    `,
    faqs: [
      { question: "How many hours does a fractional CFO work?", answer: "Typical engagements range from 10-40 hours per month." },
      { question: "What's the cost of a fractional CFO?", answer: "Expect ₹3,00,000-₹15,00,000 per month depending on experience." },
    ],
  },
  "cash-flow-management-startups": {
    title: "Cash Flow Management: The Founder's Survival Guide",
    excerpt: "90% of startups fail due to cash flow issues.",
    category: "Finance",
    author: "Victoria Sterling",
    authorRole: "Managing Partner",
    date: "November 20, 2025",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1734971702269-60d36fc28829?w=1200&q=80",
    content: `
      <p>Cash is the lifeblood of any startup. Understanding and managing cash flow is perhaps the most critical skill for any founder.</p>

      <h2>The Cash Flow Basics</h2>
      <p>Cash flow is not the same as revenue or profit. It's the actual movement of money in and out of your business.</p>

      <h2>Key Metrics to Monitor</h2>
      <ul>
        <li><strong>Runway:</strong> How many months can you operate?</li>
        <li><strong>Burn Rate:</strong> Net cash consumed per month</li>
        <li><strong>Cash Conversion Cycle:</strong> Time between paying and receiving</li>
      </ul>

      <blockquote>"Revenue is vanity, profit is sanity, but cash is reality."</blockquote>

      <h2>When to Raise vs. Cut</h2>
      <p>Running low on cash forces a critical decision: raise more capital or cut expenses.</p>
    `,
    faqs: [
      { question: "How much runway should I maintain?", answer: "Aim for 12-18 months of runway." },
      { question: "How often should I review cash flow?", answer: "Weekly reviews are recommended." },
    ],
  },
};
